import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { marketingService } from '../services/marketingService';
import './MarketingPage.css';
import { createRoot } from 'react-dom/client';
import ProductCard from '../components/ProductCard';
import { Helmet } from 'react-helmet-async';

// Helper function to decode HTML entities
const decodeHtmlEntities = (text) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const MarketingPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);
  const [relatedPages, setRelatedPages] = useState([]);
  const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://lillaflora.nu';
  const normalizedSlug = page?.slug ? page.slug.replace(/^\//, '') : '';
  const marketingTitle = page?.seo_title || page?.title || 'Inspiration från Lillaflora';
  const marketingDescription =
    page?.seo_description ||
    page?.excerpt ||
    'Guidade stylingtips, presentinspiration och romantiska orderingar direkt från vårt team i Kramfors.';
  const marketingKeywords = page?.seo_keywords || 'lillaflora, kramfors, inredning, presenter, floristbloggen';
  const marketingImage = page?.featured_image
    ? page.featured_image.startsWith('http')
      ? page.featured_image
      : `${baseOrigin}${page.featured_image.startsWith('/') ? '' : '/'}${page.featured_image}`
    : `${baseOrigin}/static/images/default-page.jpg`;
  const canonicalUrl = normalizedSlug ? `${baseOrigin}/${normalizedSlug}` : baseOrigin;

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body?.setAttribute('data-page-type', 'marketing');
    }

    return () => {
      document.body?.removeAttribute('data-page-type');
    };
  }, [slug]);

  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);
        const pageData = await marketingService.getBySlug(slug);
        setPage(pageData);

        if (pageData) {
          // Inject ItemList JSON-LD for product card placeholders
          try {
            const container = contentRef.current;
            if (container) {
              const slots = container.querySelectorAll('[data-product-card]');
              const items = Array.from(slots).map((el, idx) => {
                  const s = el.getAttribute('data-product-card');
                  return {
                    '@type': 'ListItem',
                    position: idx + 1,
                    url: `${baseOrigin}/produkter/${s}`
                  };
                });
              const scriptId = 'marketing-itemlist-jsonld';
              const prev = document.getElementById(scriptId);
              if (prev) prev.remove();
              if (items.length) {
                const sd = {
                  '@context': 'https://schema.org',
                  '@type': 'ItemList',
                  itemListElement: items
                };
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.id = scriptId;
                script.text = JSON.stringify(sd);
                document.head.appendChild(script);
              }
            }
          } catch (e) {
            console.warn('ItemList JSON-LD injection skipped:', e);
          }
          try {
            const breadcrumbId = 'marketing-breadcrumb-jsonld';
            const prevBreadcrumb = document.getElementById(breadcrumbId);
            if (prevBreadcrumb) {
              prevBreadcrumb.remove();
            }
                const breadcrumb = {
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: 'Hem',
                      item: 'https://lillaflora.nu/'
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: pageData.title,
                      item: `${baseOrigin}/${pageData.slug}`
                    }
                  ]
                };
            const breadcrumbScript = document.createElement('script');
            breadcrumbScript.type = 'application/ld+json';
            breadcrumbScript.id = breadcrumbId;
            breadcrumbScript.text = JSON.stringify(breadcrumb);
            document.head.appendChild(breadcrumbScript);
          } catch (e) {
            console.warn('Breadcrumb JSON-LD injection skipped:', e);
          }
        }
      } catch (err) {
        setError('Sidan kunde inte laddas');
        console.error('Error loading marketing page:', err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadPage();
    }
  }, [slug]);

  useEffect(() => {
    let isCancelled = false;
    const loadRelated = async () => {
      try {
        const pages = await marketingService.getAll();
        if (isCancelled) return;
        const filtered = pages
          .filter(
            (item) =>
              item &&
              item.is_active &&
              item.slug &&
              item.slug !== slug
          )
          .slice(0, 3);
        setRelatedPages(filtered);
      } catch (relatedError) {
        console.warn('Unable to load related marketing pages', relatedError);
      }
    };

    loadRelated();
    return () => {
      isCancelled = true;
    };
  }, [slug]);

  // Hydrate product cards from placeholders
  useEffect(() => {
    if (!page || !contentRef.current) return;
    const container = contentRef.current;
    const slots = container.querySelectorAll('[data-product-card]');
    slots.forEach((el) => {
      const s = el.getAttribute('data-product-card');
      if (!s) return;
      // Avoid double mounting
      if (el.getAttribute('data-hydrated') === 'true') return;
      el.setAttribute('data-hydrated', 'true');
      const root = createRoot(el);
      root.render(<ProductCard slug={s} />);
    });
  }, [page]);

  if (loading) {
    return (
      <div className="marketing-page-loading">
        <div className="spinner"></div>
        <p>Laddar...</p>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="marketing-page-error">
        <h1>404 - Sidan hittades inte</h1>
        <p>{error || 'Den här sidan existerar inte.'}</p>
      </div>
    );
  }

  return (
    <div className="marketing-page">
      <Helmet>
        <title>{marketingTitle}</title>
        <meta name="description" content={marketingDescription} />
        <meta name="keywords" content={marketingKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={marketingTitle} />
        <meta property="og:description" content={marketingDescription} />
        <meta property="og:image" content={marketingImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Lillaflora" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={marketingTitle} />
        <meta name="twitter:description" content={marketingDescription} />
        <meta name="twitter:image" content={marketingImage} />
      </Helmet>
      <div className="marketing-header">
        <div className="container">
          <h1>{decodeHtmlEntities(page.title)}</h1>
          {page.excerpt && <p className="marketing-excerpt">{decodeHtmlEntities(page.excerpt)}</p>}
        </div>
      </div>

          <div className="marketing-content">
        <div className="container">
          {page.featured_image && (
            <img
              loading="lazy"
              src={page.featured_image}
              alt={decodeHtmlEntities(page.title)}
              className="marketing-featured-image"
            />
          )}

          <div
            className="marketing-content-text"
            ref={contentRef}
            dangerouslySetInnerHTML={{ __html: page.content }}
          />

          {page.cta_text && page.cta_link && (
            <div className="marketing-cta">
              <a
                href={page.cta_link}
                className="cta-button"
                target={page.cta_link.startsWith('http') ? '_blank' : '_self'}
                rel={page.cta_link.startsWith('http') ? 'noopener noreferrer' : ''}
              >
                {decodeHtmlEntities(page.cta_text)}
              </a>
            </div>
          )}

          {relatedPages.length > 0 && (
            <section className="marketing-related-reading">
              <div className="container">
                <h2>Mer inspiration från våra guider</h2>
                <div className="related-grid">
                  {relatedPages.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/${related.slug}`}
                      className="related-card"
                    >
                      {related.featured_image && (
                        <div
                          className="related-card-image"
                          style={{
                            backgroundImage: `url(${related.featured_image})`
                          }}
                          aria-hidden="true"
                        />
                      )}
                      <div className="related-card-content">
                        <h3>{decodeHtmlEntities(related.title)}</h3>
                        {related.excerpt && (
                          <p>{decodeHtmlEntities(related.excerpt)}</p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Generic internal links to key categories and blog for stronger SEO interlinking */}
          <div className="marketing-related-links">
            <h2>Utforska fler produkter</h2>
            <ul>
              <li>
                <Link to="/produkter?kategori=doftljus-och-doftpinnar">
                  Ljus &amp; doftljus för hemmamys
                </Link>
              </li>
              <li>
                <Link to="/produkter?kategori=dukning-och-servering">
                  Dukning &amp; servering – perfekta gå bort‑presenter
                </Link>
              </li>
              <li>
                <Link to="/produkter?kategori=textiler">
                  Mjuka textilier som kuddar, plädar och kökstextilier
                </Link>
              </li>
              <li>
                <Link to="/floristbloggen">
                  Läs fler presenttips och stylingidéer i Floristbloggen
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingPage;
